import React, { useCallback, useState, useEffect, useMemo } from "react";
import { cloneDeep, isNil, size } from "lodash";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Button,
  Typography,
  Collapse,
  TextField,
  Tooltip,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";

import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as ChevronSolidIcon } from "assets/images/icons/icn_chevron_solid.svg";
import { ReactComponent as UnselectedIcon } from "assets/images/icons/icn_unselected.svg";
import { ReactComponent as SelectedIcon } from "assets/images/icons/icn_selected.svg";
import { ReactComponent as SwitchOnIcon } from "assets/images/icons/Toggle-On.svg";
import { ReactComponent as SwitchOffIcon } from "assets/images/icons/Toggle-Off.svg";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show_hover.svg";
import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as CheckDisabledIcon } from "assets/images/icons/checkbox_disabled.svg";
import icon_snmp from 'assets/images/icons/icn_Info_solid.svg';
import icon_snmp_hover from 'assets/images/icons/icn_info_solid_hover.svg';

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import ModalContainer from "components/ModalContainer";
import { MenuSelect } from "components/Select/menuSelect";
import GlobalAlert from "components/Notification";

// Global const
import { formLimitation, passwordRegex } from 'const';

// Local const
import { defaultFormData, SECURITY_ALLOWED_6G,  SECURITY_ALLOWED_MLO, PMF_MAP } from "../../const";

const CreateNewWirelessNetworkModal = ({
  setAlertInfo,
  wirelessData,
  setWirelessData,
  onUpdateBlockerData,
  bandOptions,
  openCreateModal,
  setOpenCreateModal,
  securityTypeAssembleList,
  securityTypeConfig,
  radiusProfileOptions,
  siteId,
  orgId,
  entId,
  hasManageOrg,
  getWirelessSSID,
  isValidInputStyles,
  captivePortalOptions,
  bandCount,
}) => {
  const theme = useTheme();

  const [formData, setFormData] = useState(cloneDeep(defaultFormData));
  const [hasDuplicateName, setHasDuplicateName] = useState(false);

  const [securityList, setSecurityList] = useState(
    structuredClone(securityTypeAssembleList)
  );
  const [openGeneral, setOpenGeneral] = useState(true);
  const [openAdvancedSettings, setOpenAdvancedSettings] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const ssidNameSize = useMemo(() => size(formData?.ssid?.trim()), [size, formData?.ssid]);

  const showPasswordSetting = useMemo(() => {
    return securityTypeConfig[formData?.security]?.isPasswordRequired;
  }, [formData?.security]);

  const showRadiusProfileSetting = useMemo(() => {
    return securityTypeConfig[formData?.security]?.isRadiusProfileRequired;
  }, [formData?.security]);

  const isPasswordValid = useMemo(() => {
    let isValid = true;

    if (!formData) return isValid;

    const isPasswordRequiredSecurityMode =
      securityTypeConfig[formData?.security]?.isPasswordRequired;
    const isPasswordValidByRegexp = passwordRegex.wireless.ssid.test(formData?.password);

    if (isPasswordRequiredSecurityMode && !isPasswordValidByRegexp)
      isValid = false;
    else if (
      !isPasswordRequiredSecurityMode &&
      formData?.password &&
      !isPasswordValidByRegexp
    )
      isValid = false;

    return isValid;
  }, [formData?.security, formData?.password]);

  const handleCloseModal = () => {
    setOpenCreateModal(false);
  };

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const onSave = () => {
    const tmpCreateId = uuidv4();
    const newWirelessData = [ ...wirelessData, { ...formData, isWaitingCreate: true, tmpCreateId }];

    setWirelessData(structuredClone(newWirelessData));
    onUpdateBlockerData(newWirelessData);

    setOpenCreateModal(false);
    setShowPassword(false);

    setAlertInfo({
      open: true,
      severity: "success",
      message: (
        <Box>
          <Box
            sx={{
              fontSize: theme.typography.customFontSize[3],
              fontWeight: theme.typography.customFontWeight[3],
              pb: 1
            }}
          >
            Changes Saved
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography>Settings saved to draft. Click</Typography>
            <Typography sx={{ fontWeight: theme.typography.customFontWeight[3] }}> "Apply Changes" </Typography>
            <Typography>to deploy them to the device.</Typography>
          </Box>
        </Box>
      )
    });

  };

  const isFormInvalid = () => {
    let isInvalid = false;

    if (!formData?.ssid || ssidNameSize < 1 || ssidNameSize > 32 || hasDuplicateName) isInvalid = true;
    if (!formData?.band?.length) isInvalid = true;
    if (!formData?.security) isInvalid = true;
    if (
      !formData?.vlanId ||
      formData?.vlanId < formLimitation.vlanId.min ||
      formData?.vlanId > formLimitation.vlanId.max
    )
      isInvalid = true;
    if (showPasswordSetting && !isPasswordValid) isInvalid = true;
    if (formData?.captivePortalName === "") isInvalid = true;

    const isSupportRadiusProfile = ["WPA/WPA2-ENTERPRISE", "WPA2-ENTERPRISE", "WPA3-ENTERPRISE"].includes(formData.security);
    const hasRadiusProfile = radiusProfileOptions.find(item => item?.value === formData?.radiusProfile);
    if (isSupportRadiusProfile && !hasRadiusProfile) {
      isInvalid = true;
    }

    return isInvalid;
  };

  const textfieldStyle = {
    width: "286px",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "30px",
      "& fieldset": {
        borderColor: theme.palette.primary.borderGreyPrimary,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.mainHover,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const rowStyle = {
    paddingLeft: "32px",
    marginTop: "20px",
    display: "flex",
    alignItems: "center",
    gap: 2,
  };

  const labelStyle = {
    width: "180px",
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[0],
  };

  const readOnlyStyle = {
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[1],
  };

  const getAvailableSecurities = useCallback(({ bands }) => {
    const has6G = bands.includes('6');

    if (bands.length === 0) {
      return securityTypeAssembleList;
    }

    if (has6G) {
      return securityTypeAssembleList.filter(s => SECURITY_ALLOWED_6G.includes(s.value));
    }

    return securityTypeAssembleList;
  }, []);

  useEffect(() => {
    const bands = formData?.band ?? [];
    const isMultipleBands = bands.length >= 2;
    const newSecurityList = getAvailableSecurities({ bands });
    setSecurityList(newSecurityList);

    setFormData(prev => {
      const updates = {};

      // Ensure mlo is disabled when multiple bands are not selected.
      if (!isMultipleBands) {
        updates.mloEnable = false;
      }

      // Ensure selected security is valid for the current band configuration.
      const securityValid = newSecurityList.some(s => s.value === prev.security);
      const nextSecurity = securityValid ? prev.security : newSecurityList[0]?.value ?? null;
      if (nextSecurity !== prev.security) {
        updates.security = nextSecurity;
      }

      // Disable MLO when security does not support it.
      const mloAllowed = SECURITY_ALLOWED_MLO.includes(nextSecurity);
      if (!mloAllowed && prev.mloEnable) {
        updates.mloEnable = false;
      }

      // Update wpaMode only when security changes.
      if (nextSecurity && nextSecurity !== prev.security) {
        const config = securityTypeConfig[nextSecurity];
        updates.wpaMode = config?.wpaMode?.display ?? null;
        updates.encryption = config?.encryption ?? null;
        updates.password = config?.isPasswordRequired ? prev.password : null;
      }

      return Object.keys(updates).length ? { ...prev, ...updates } : prev;
    });
  }, [formData?.band, formData?.security]);

  useEffect(() => {
    const newPmfState = PMF_MAP[formData?.wpaMode] ?? 'Disable';
    if (formData.pmfState !== newPmfState) {
      setFormData(prev => ({ ...prev, pmfState: newPmfState }));
    }
  }, [formData?.wpaMode]);

  useEffect(() => {
    if (!openCreateModal) {
      setFormData(cloneDeep(defaultFormData));
      setSecurityList(structuredClone(securityTypeAssembleList));
      setShowPassword(false);
      setHasDuplicateName(false);
      setOpenGeneral(true);
      setOpenAdvancedSettings(true);
    }
  }, [openCreateModal]);

  return (
    <>
      <ModalContainer display={openCreateModal} modalWidth="750px">
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <CircularProgress sx={{ color: theme.palette.primary.mainHover }} />
          </Box>
        )}
        <Box sx={{ padding: "0 10px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.customFontSize[5],
                fontWeight: theme.typography.customFontWeight[3],
              }}
            >
              Create New Wireless Network
            </Typography>
            <Button
              onClick={handleCloseModal}
              sx={{
                backgroundColor: theme.palette.primary.transparent,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: theme.palette.primary.transparent,
                },
              }}
            >
              <CloseIcon />
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingTop: "40px",
              cursor: "pointer",
              gap: "8px",
            }}
            onClick={() => setOpenGeneral(!openGeneral)}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                transform: openGeneral ? "rotate(90deg)" : "rotate(0deg)",
              }}
            >
              <ChevronSolidIcon />
            </Box>
            <Typography
              sx={{
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[1],
              }}
            >
              General
            </Typography>
          </Box>

          <Collapse in={openGeneral}>
            {/* Wi-Fi Name (SSID) */}
            <Box sx={{ ...rowStyle, marginBottom: hasDuplicateName ? '10px' : '0px' }}>
              <Box sx={{ ...labelStyle, paddingTop: hasDuplicateName ? '0px' : '10px', height: '44px' }}>Wi-Fi Name (SSID)</Box>
              <TextField
                placeholder="e.g. My Network"
                sx={textfieldStyle}
                autoComplete="off"
                value={formData.ssid || ''}
                onChange={e => {
                  setFormData((prev) => ({ ...prev, ssid: e.target.value }))
                  setHasDuplicateName(wirelessData?.some(item => item?.ssid === e.target.value));
                }}
                error={hasDuplicateName || (!isNil(formData?.ssid) && ( ssidNameSize < 1 || ssidNameSize > 32 ))}
                helperText={hasDuplicateName ? 'This name already exists in this site.' : ''}
              />
            </Box>
            {/* Band */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>Band</Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                {Object.keys(bandOptions).map((band, index) => (
                  <Box
                    key={band}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Checkbox
                      sx={{ paddingLeft: index === 0 ? '0px' : '9px' }}
                      checked={!!formData?.band?.includes(band)}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const newBand = (
                            e.target.checked
                              ? [...prev.band, band]
                              : prev.band.filter((v) => v !== band)
                          ).sort((a, b) => a - b);

                          return { ...prev, band: newBand };
                        })
                      }
                      icon={bandCount?.[band] >= 8 ? <CheckDisabledIcon style={{ width: 24, height: 24, paddingTop: 2 }} /> : <UnselectedIcon />}
                      checkedIcon={<SelectedIcon />}
                      disableRipple
                      disabled={bandCount?.[band] >= 8}
                    />
                    <Typography
                      sx={{
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[2],
                      }}
                    >
                      {bandOptions[band]} GHz
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            {/* MLO */}
            <Box sx={rowStyle}>
              <Typography sx={{ ...labelStyle, display: 'flex' }}>
                MLO
                <Tooltip
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 330,
                      },
                    },
                  }}
                  title={
                    <Box>
                      <Typography variant="body2" fontSize={13}>
                        Enable Multi-Link Operation (MLO) to let Wi-Fi 7 clients connect across multiple bands and switch smoothly between links for improved performance and
                        reliability.
                      </Typography>
                      <Typography fontWeight="bold" fontSize={13}>
                        Important
                      </Typography>
                      <Typography variant="body2" fontSize={13}>
                        MLO requires Wi-Fi 7 (802.11be) and MLO-capable client devices.
                      </Typography>
                      <Typography variant="body2" fontSize={13}>
                        The SSID enabled MLO only supports WPA3 or Enhanced Open clients.
                      </Typography>
                    </Box>
                  }
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: 16,
                      marginLeft: 1,
                      '& img': {
                        content: `url(${icon_snmp})`,
                      },
                      '&:hover img': {
                        content: `url(${icon_snmp_hover})`,
                      },
                    }}
                  >
                    <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  </Box>
                </Tooltip>
              </Typography>
              <Box
                sx={{ width: 40, height: 22, display: 'flex', alignItems: 'center' }}
                onClick={() => setFormData(prev => ({ ...prev, mloEnable: !prev?.mloEnable }))}
              >
                <IconButton
                  sx={{
                    paddingLeft: 0,
                    cursor: (formData?.band?.length < 2 || !SECURITY_ALLOWED_MLO.includes(formData?.security)) ? 'default' : 'pointer',
                  }}
                  onClick={(e) => (formData?.band?.length < 2 || !SECURITY_ALLOWED_MLO.includes(formData?.security)) && e.stopPropagation()}
                >
                  {formData?.mloEnable ? <SwitchOnIcon /> : <SwitchOffIcon />}
                </IconButton>
              </Box>
            </Box>
            {/* Security */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>Security</Box>
              <MenuSelect
                value={formData?.security || ''}
                onChange={(opt) => {
                  const config = securityTypeConfig[opt.value] || {
                    wpaMode: "",
                    encryption: "",
                  };
                  setFormData((prev) => ({
                    ...prev,
                    security: opt.value,
                    wpaMode: config.wpaMode.display,
                    encryption: config.encryption,
                    password: config.isPasswordRequired ? prev.password : null,
                    radiusProfile: config.isRadiusProfileRequired ? radiusProfileOptions?.[0]?.value : null, // TODO: 拿掉 Disabled 選項
                  }));
                }}
                options={securityList.filter((security) => security.isShow)}
                width={textfieldStyle.width}
              />
            </Box>
            {/* RADIUS Profile */}
            {showRadiusProfileSetting && (
              <Box sx={rowStyle}>
                <Box sx={labelStyle}>RADIUS Profile</Box>
                <MenuSelect
                  value={formData?.radiusProfile || ''}
                  options={radiusProfileOptions}
                  width={textfieldStyle.width}
                  onChange={(opt) => {
                    setFormData((prev) => ({
                      ...prev,
                      radiusProfile: opt?.value ?? null,
                    }));
                  }}
                />
              </Box>
            )}
            {/* PMF */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>PMF</Box>
              <Typography sx={readOnlyStyle}>{formData?.pmfState || 'Disable'}</Typography>
            </Box>
            {/* WPA Mode */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>WPA Mode</Box>
              <Typography sx={readOnlyStyle}>
                {formData.wpaMode || "-"}
              </Typography>
            </Box>
            {/* Encryption Type */}
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>Encryption Type</Box>
              <Typography sx={readOnlyStyle}>
                {formData.encryption || "-"}
              </Typography>
            </Box>
            {/* Wi-Fi Password */}
            {showPasswordSetting && (
              <>
                <Box sx={rowStyle}>
                  <Box sx={labelStyle}>Wi-Fi Password</Box>
                  <TextField
                    type={showPassword ? "text" : "password"}
                    sx={{
                      ...textfieldStyle,
                      ...isValidInputStyles(
                        formData?.password == null || isPasswordValid
                      ),
                      width: textfieldStyle.width,
                    }}
                    autoComplete="off"
                    value={formData.password || ''}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }));
                    }}
                    placeholder="Enter Password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={toggleShowPassword}
                            edge="end"
                            sx={{
                              padding: 1,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.primary.transparent,
                              },
                              "&.Mui-focusVisible": {
                                backgroundColor:
                                  theme.palette.primary.transparent,
                              },
                            }}
                          >
                            {showPassword ? <ShowIcon /> : <HideIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </>
            )}

            {showPasswordSetting && !!formData.password && !isPasswordValid && (
              <Box
                sx={{
                  ...rowStyle,
                  marginTop: "3px",
                }}
              >
                <Typography
                  sx={{
                    color: theme.palette.primary.redPrimary,
                    fontSize: "0.75rem",
                    marginLeft: "12.3rem",
                  }}
                >
                  Minimum length: 8 characters. Allowed symbols: ~ ! [ ] ; ' \ : @ # $ & *
                </Typography>
              </Box>
            )}

            <Box sx={rowStyle}>
              <Box sx={labelStyle}>VLAN ID</Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  type="number"
                  autoComplete="off"
                  value={formData.vlanId || ""}
                  sx={{
                    ...textfieldStyle,
                    ...isValidInputStyles(
                      formData?.vlanId === defaultFormData.vlanId ||
                      (formData?.vlanId >= formLimitation.vlanId.min && formData?.vlanId <= formLimitation.vlanId.max)
                    ),
                    width: textfieldStyle.width,
                  }}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      vlanId: +e.target.value,
                    }))
                  }
                />
                <Typography
                  sx={{
                    color: theme.palette.primary.greySecound,
                    fontSize: theme.typography.customFontSize[1],
                  }}
                >
                  {/* (1~4094) */}
                  {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                </Typography>
              </Box>
            </Box>
          </Collapse>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingTop: "40px",
              cursor: "pointer",
              gap: "8px",
            }}
            onClick={() => setOpenAdvancedSettings(!openAdvancedSettings)}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                transform: openAdvancedSettings
                  ? "rotate(90deg)"
                  : "rotate(0deg)",
              }}
            >
              <ChevronSolidIcon />
            </Box>
            <Typography
              sx={{
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[1],
              }}
            >
              Advanced Settings
            </Typography>
          </Box>

          <Collapse in={openAdvancedSettings}>
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>SSID Broadcast</Box>
              <Box
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    broadcastSSID: !prev.broadcastSSID,
                  }))
                }
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  width: 40,
                  height: 22,
                }}
              >
                {formData.broadcastSSID ? <SwitchOnIcon /> : <SwitchOffIcon />}
              </Box>
            </Box>
            <Box sx={rowStyle}>
              <Box sx={labelStyle}>Captive Portal</Box>
              <MenuSelect
                value={formData?.captivePortalName ?? "disabled"}
                options={captivePortalOptions}
                width={textfieldStyle.width}
                onChange={(opt) => {
                  setFormData((prev) => ({
                    ...prev,
                    captivePortalName:
                      opt?.value === "disabled" ? null : opt?.value ?? null,
                  }));
                }}
              />
            </Box>
          </Collapse>

          <Box
            sx={{
              margin: "50px auto 30px",
              display: "flex",
              justifyContent: "center",
              gap: "20px",
            }}
          >
            <OutlineButton
              onClick={handleCloseModal}
              width="100px"
              height="32px"
            >
              Cancel
            </OutlineButton>
            <SolidButton
              disabled={isFormInvalid()}
              onClick={onSave}
              width="100px"
              height="32px"
            >
              Save
            </SolidButton>
          </Box>
        </Box>
      </ModalContainer>
    </>
  );
};

export default CreateNewWirelessNetworkModal;
